import { DREnums } from 'containers/Esrs/pieces/DisclosureRequirements';
import { Paragraph, Bookmark, TextRun } from 'docx';
import {
  EsrsReportDisclosureRequirement,
  EsrsReportMetric,
  EsrsReportStandard,
} from '../../Report.types';
import { S1_6Metrics, S1_8Metrics, E1_5Metrics } from '../../ReportComponents';
import { createS18CountriesCoverageTable } from '../CustomTables/countries-coverage';
import { E1_6TargetMetrics, createE16TargetsTable } from '../CustomTables/E1-6-targets';
import { createE15Table } from '../CustomTables/energy-consumption-mix';
import { createS16Tables } from '../CustomTables/S1-6-tables';
import { addEmptySpace } from '../report-helper-functions';
import { TextStyle } from '../reportThemes';
import { createMetrics } from './metrics';
import { createMdrm } from './mdrm';
import { createTargets } from './targets';
import { createActions } from './actions';

export const createDisclosures = (
  disclosureRequirements: EsrsReportDisclosureRequirement[],
  companyCurrency: string,
  targetQuestions: EsrsReportStandard['targetQuestions'],
  actionQuestions: EsrsReportStandard['actionQuestions'],
  standardRef: string,
  year: number
) => {
  const visibleDisclosureRequirements = disclosureRequirements.filter((dr) =>
    dr.metricsIncludingOptedOut.some((metric) => !metric.metric.isHidden)
  );
  const displayedDisclosureRequirements = visibleDisclosureRequirements
    .sort((a, b) => a.order - b.order)
    .flatMap((dr) => {
      const isEntitySpecific = dr.additionalTypes?.some(
        (type) => type.additionalTypeRef === 'Entity-specific'
      );
      const titleParagraph = new Paragraph({
        children: [
          new Bookmark({
            id: dr.reference,
            children: [
              new TextRun({
                text:
                  isEntitySpecific || dr.reference === 'Action plans' || dr.reference === 'Targets'
                    ? `${dr.title}`
                    : `${dr.reference} – ${dr.title}`,
              }),
            ],
          }),
        ],
        style: TextStyle.h2,
        spacing: {
          after: 150,
        },
      });
      const isTarget = dr.type === DREnums.target;
      const isAction = dr.type === DREnums.action;
      const isPolicy = dr.type === DREnums.policy;

      if (isTarget || isAction || isPolicy) {
        const withAssociationMetrics =
          dr.metrics.filter((metric) => metric.metric.metric.hasAssociation === true) ?? [];
        const withoutAssociationMetrics =
          dr.metrics.filter((metric) => metric.metric.metric.hasAssociation === false) ?? [];

        const withAssociationMetricsArray = withAssociationMetrics?.length
          ? createMetrics(withAssociationMetrics, companyCurrency)
          : [];

        const withoutAssociationMetricsArray = withoutAssociationMetrics?.length
          ? createMetrics(withoutAssociationMetrics, companyCurrency)
          : [];

        return [
          titleParagraph,
          ...withoutAssociationMetricsArray,
          isTarget
            ? createTargets(dr.targets, targetQuestions, standardRef, companyCurrency)
            : createActions(dr.actions, actionQuestions, companyCurrency),
          ...withAssociationMetricsArray,
        ];
      } else {
        if (dr.reference === 'E1-6') {
          const targets = disclosureRequirements.find((dr) => dr.reference === 'E1-4')?.targets;
          const mdrmMetrics = dr.metrics.filter(
            (m) =>
              E1_6TargetMetrics.map((m) => m.reference).includes(m.metric.metricRef) ||
              m.metric.metricRef === 'grossScopeGHG2'
          );
          const otherMetrics = dr.metrics.filter(
            (m) =>
              ![...E1_6TargetMetrics, { reference: 'grossScopeGHG2' }].some(
                (tm) => tm.reference === m.metric.metricRef
              )
          );

          return [
            titleParagraph,
            addEmptySpace(100),
            createE16TargetsTable(
              targets,
              dr.metrics.map((m) => m.tableData),
              year
            ),
            addEmptySpace(200),
            mdrmMetrics.flatMap((metric) => metric && createMdrm(metric)),
            addEmptySpace(200),
            createMetrics(otherMetrics, companyCurrency),
            addEmptySpace(200),
          ];
        } else if (dr.reference === 'S1-6') {
          const metrics = dr.metrics.filter(
            (m) =>
              !S1_6Metrics.includes(m.metric.metricRef) || m.metric.metricRef === 'totalEmployees'
          );
          const customMetrics = dr.metrics.filter((m) => S1_6Metrics.includes(m.metric.metricRef));
          return [
            titleParagraph,
            addEmptySpace(200),
            ...createS16Tables(customMetrics, companyCurrency),
            addEmptySpace(200),
            customMetrics.flatMap((metric) => createMdrm(metric)),
            addEmptySpace(200),
            createMetrics(metrics, companyCurrency),
            addEmptySpace(200),
          ];
        } else if (
          dr.reference === 'S1-8' &&
          dr.metrics.some((m) => S1_8Metrics.includes(m.metric.metricRef))
        ) {
          const S1_8MetricsData =
            dr.metrics.filter((m) => S1_8Metrics.includes(m.metric.metricRef)) ??
            ([] as EsrsReportMetric[]);
          const otherMetrics = (dr.metrics.filter(
            (m) => !S1_8Metrics.includes(m.metric.metricRef)
          ) ?? []) as EsrsReportMetric[];
          return [
            titleParagraph,
            addEmptySpace(200),
            createS18CountriesCoverageTable(S1_8MetricsData),
            addEmptySpace(200),
            S1_8MetricsData.flatMap((metric) => createMdrm(metric)),
            addEmptySpace(200),
            createMetrics(otherMetrics, companyCurrency),
            addEmptySpace(200),
            ,
          ];
        } else if (dr.reference === 'E1-5') {
          const E1_5MetricsData = (dr.metrics.filter((m) =>
            E1_5Metrics.includes(m.metric.metricRef)
          ) ?? []) as EsrsReportMetric[];
          const otherMetrics = (dr.metrics.filter(
            (m) => !E1_5Metrics.includes(m.metric.metricRef)
          ) ?? []) as EsrsReportMetric[];

          return [
            titleParagraph,
            addEmptySpace(100),
            createE15Table(E1_5MetricsData, year),
            addEmptySpace(200),
            E1_5MetricsData.flatMap((metric) => createMdrm(metric)),
            addEmptySpace(200),
            createMetrics(otherMetrics, companyCurrency),
            addEmptySpace(200),
          ];
        }
        return [titleParagraph, createMetrics(dr.metrics, companyCurrency)];
      }
    });
  return displayedDisclosureRequirements.flat();
};
